import { useState } from 'react';
import { Check, FolderKanban } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { AutoAssignedBadge } from '@/components/shared/AutoAssignedBadge';
import { t } from '@/i18n';
import type { ProjectAssignedBy, ProjectOption } from '@ip/shared-types';

interface InvoiceProjectPickerProps {
  /** All company projects (closed included — needed to render a closed current value). */
  projects: ProjectOption[];
  projectId: string | null;
  /** Server-provided name, used when the project is missing from `projects`. */
  projectName: string | null;
  assignedBy: ProjectAssignedBy | null;
  /**
   * Some lines carry their own projects. The picker stays usable (the
   * invoice's project receives the residual — everything the lines didn't
   * take); a "partial" badge explains the split.
   */
  hasLineProjects: boolean;
  onChange: (projectId: string | null) => void;
}

/**
 * Compact whole-invoice project control for the invoice-detail header —
 * sits next to InvoiceTagsEditor and mirrors its chip-that-opens-a-popover
 * look. Selecting saves immediately (optimistic update lives in the parent).
 * Only ACTIVE projects are offered; a closed current assignment still
 * renders as the value. Rule-made assignments carry the "auto" badge.
 */
export function InvoiceProjectPicker({
  projects,
  projectId,
  projectName,
  assignedBy,
  hasLineProjects,
  onChange,
}: InvoiceProjectPickerProps) {
  const [open, setOpen] = useState(false);

  const current = projectId ? projects.find((p) => p.id === projectId) : undefined;
  const currentName = current?.name ?? projectName;
  const activeProjects = projects.filter((p) => p.status === 'active');

  const select = (id: string | null) => {
    setOpen(false);
    if (id !== projectId) onChange(id);
  };

  return (
    <span className="inline-flex items-center gap-1.5">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label={t('invoices.bulk.assignToProject')}
            className={
              projectId
                ? 'hover:bg-accent inline-flex max-w-56 cursor-pointer items-center gap-1.5 rounded-md border px-2 py-0.5 text-xs'
                : 'text-muted-foreground hover:text-foreground inline-flex cursor-pointer items-center gap-1 rounded-md border border-dashed px-2 py-0.5 text-xs'
            }
          >
            {projectId ? (
              <>
                <span
                  className="inline-block size-2 shrink-0 rounded-full border"
                  style={{ backgroundColor: current?.color ?? 'transparent' }}
                  aria-hidden="true"
                />
                <span className="truncate" title={currentName ?? undefined}>
                  {currentName ?? projectId}
                  {current && current.status !== 'active'
                    ? ` ${t('projects.selector.closedSuffix')}`
                    : ''}
                </span>
              </>
            ) : (
              <>
                <FolderKanban className="size-3" />
                {t('projects.selector.noProject')}
              </>
            )}
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-64 p-1" align="start">
          <button
            type="button"
            className="hover:bg-accent flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm"
            onClick={() => select(null)}
          >
            <span className="flex size-4 shrink-0 items-center justify-center">
              {!projectId && <Check className="size-3.5" />}
            </span>
            <span className="text-muted-foreground">{t('projects.selector.noProject')}</span>
          </button>
          {activeProjects.map((project) => (
            <button
              key={project.id}
              type="button"
              className="hover:bg-accent flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm"
              onClick={() => select(project.id)}
            >
              <span className="flex size-4 shrink-0 items-center justify-center">
                {projectId === project.id && <Check className="size-3.5" />}
              </span>
              <span
                className="inline-block size-2 shrink-0 rounded-full border"
                style={{ backgroundColor: project.color ?? 'transparent' }}
                aria-hidden="true"
              />
              <span className="truncate">{project.name}</span>
            </button>
          ))}
          {/* A closed (or otherwise non-active) current assignment stays
              visible in the list — selecting it again is a no-op. */}
          {projectId && !activeProjects.some((p) => p.id === projectId) && (
            <button
              type="button"
              className="hover:bg-accent flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm"
              onClick={() => select(projectId)}
            >
              <span className="flex size-4 shrink-0 items-center justify-center">
                <Check className="size-3.5" />
              </span>
              <span
                className="inline-block size-2 shrink-0 rounded-full border"
                style={{ backgroundColor: current?.color ?? 'transparent' }}
                aria-hidden="true"
              />
              <span className="text-muted-foreground truncate">
                {currentName ?? projectId} {t('projects.selector.closedSuffix')}
              </span>
            </button>
          )}
          {activeProjects.length === 0 && !projectId && (
            <p className="text-muted-foreground px-2 py-1.5 text-sm">
              {t('projects.noProjects')}
            </p>
          )}
        </PopoverContent>
      </Popover>
      {/* Provenance: rule-made assignments get the "auto" hint. */}
      {projectId && assignedBy === 'rule' && <AutoAssignedBadge />}
      {/* Mixed invoice: some lines carry their own projects; the invoice's
          project only receives the residual. */}
      {hasLineProjects && (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="text-muted-foreground inline-flex items-center gap-1 rounded-md border border-dashed px-1.5 py-0.5 text-xs italic">
              {t('projects.partialBadge')}
            </span>
          </TooltipTrigger>
          <TooltipContent className="max-w-64">{t('projects.partialTooltip')}</TooltipContent>
        </Tooltip>
      )}
    </span>
  );
}
